import { Button, ButtonWithIconProps, Spacer, Text } from '@oy/ui';
import { Colors } from '@oy/uicore';
import { ProductFeatureIcon, SystemIcon } from '@oy/uiicon';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styles from './TooltipButtonWithIcon.module.css';

interface TooltipButtonWithIconProps extends ButtonWithIconProps {
  errorTitle?: string;
  errorReason?: string;
  isPinned?: boolean;
}

const ICON_COLORS = {
  [Button.Color.Primary]: Colors.BLUE100,
  [Button.Color.Secondary]: Colors.BLACK100,
  [Button.Color.Destructive]: Colors.RED100,
};

const ICON_SIZES = {
  [Button.Size.Small]: 16,
  [Button.Size.Medium]: 20,
  [Button.Size.Large]: 24,
};

const TooltipButtonWithIcon: React.FC<TooltipButtonWithIconProps> = ({
  productFeatureIconName,
  iconName,
  errorTitle,
  errorReason,
  children,
  size = Button.Size.Medium,
  isPinned = false,
  ...props
}) => {
  const tooltipId = `tooltip-button-${props.id}`;
  const iconColor =
    props.buttonType === Button.Type.Filled
      ? Colors.WHITE
      : ICON_COLORS[props.color as keyof typeof ICON_COLORS];

  const iconSize = ICON_SIZES[size];

  const childrenSpacer =
    size === Button.Size.Large ? Spacer.Size.S : Spacer.Size.XS;

  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  if (isPinned) {
    const handleMouseEnter = (e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setTooltipPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    };

    const handleMouseLeave = () => {
      setTooltipPosition(null);
    };

    return (
      <>
        <Button {...props}>
          {children && <Spacer right={childrenSpacer}>{children}</Spacer>}
          <div
            data-tooltip-id={tooltipId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {productFeatureIconName ? (
              <ProductFeatureIcon
                color={iconColor}
                name={productFeatureIconName}
                size={iconSize}
              />
            ) : (
              <SystemIcon color={iconColor} name={iconName} size={iconSize} />
            )}
            {tooltipPosition &&
              (errorTitle || errorReason) &&
              createPortal(
                <div
                  className={styles.tooltipCustom}
                  style={{
                    position: 'absolute',
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    transform: 'translateX(-40%) translateY(10%)',
                    zIndex: 9999,
                  }}
                >
                  {errorTitle && (
                    <Text
                      color={Colors.WHITE}
                      textStyle={Text.Style.BodySmSemibold}
                    >
                      {errorTitle}
                    </Text>
                  )}
                  {errorReason && (
                    <Text
                      color={Colors.WHITE}
                      textStyle={Text.Style.BodySmRegular}
                    >
                      {errorReason}
                    </Text>
                  )}
                </div>,
                document.body,
              )}
          </div>
        </Button>
      </>
    );
  }

  return (
    <>
      <Button {...props}>
        {children && <Spacer right={childrenSpacer}>{children}</Spacer>}
        <div data-tooltip-id={tooltipId}>
          {productFeatureIconName ? (
            <ProductFeatureIcon
              color={iconColor}
              name={productFeatureIconName}
              size={iconSize}
            />
          ) : (
            <SystemIcon color={iconColor} name={iconName} size={iconSize} />
          )}
          {(errorTitle || errorReason) && (
            <Tooltip className={styles.tooltip} id={tooltipId} place="bottom">
              {errorTitle && (
                <Text
                  color={Colors.WHITE}
                  textStyle={Text.Style.BodySmSemibold}
                >
                  {errorTitle}
                </Text>
              )}
              {errorReason && (
                <Text color={Colors.WHITE} textStyle={Text.Style.BodySmRegular}>
                  {errorReason}
                </Text>
              )}
            </Tooltip>
          )}
        </div>
      </Button>
    </>
  );
};

export default TooltipButtonWithIcon;
